/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * COREX PROJECT CONFIGURATION
 * ============================================================================
 *
 * Central configuration for the CoreX Starter Kit.
 * Forked projects should update this file with their own branding.
 *
 * ============================================================================
 */

// Vite requires ES module imports for src/assets — raw string paths won't resolve
import corexLogoDark from '@/assets/logos/dark/corex-logo-dark.svg';
import corexLogoLight from '@/assets/logos/light/corex-logo-light.svg';
import ventaLogoDark from '@/assets/logos/dark/ventacorex-logo-dark.svg';
import ventaLogoLight from '@/assets/logos/light/ventacorex-logo-light.svg';
import hrLogoDark from '@/assets/logos/dark/hrcorex-logo-dark.svg';
import hrLogoLight from '@/assets/logos/light/hrcorex-logo-light.svg';
import comptaLogoDark from '@/assets/logos/dark/comptacorex-logo-dark.svg';
import comptaLogoLight from '@/assets/logos/light/comptacorex-logo-light.svg';
import pmLogoDark from '@/assets/logos/dark/pmcorex-logo-dark.svg';
import pmLogoLight from '@/assets/logos/light/pmcorex-logo-light.svg';

export interface ProjectConfig {
  // Branding
  name: string;
  tagline: string;
  description: string;
  version: string;

  // Logos
  logos: {
    dark: string;
    light: string;
    icon?: string;
  };

  // Theme
  theme: {
    default: 'dark' | 'light' | 'system';
    showTutorial: boolean;
  };

  // Auth Configuration
  auth: {
    mfa: {
      enabled: boolean;
      methods: ('TOTP' | 'EMAIL_OTP')[];
      trustDeviceDays: number;
    };
    session: {
      inactivityTimeoutMinutes: number;
      absoluteTimeoutMinutes: number;
      singleSession: boolean;
    };
    password: {
      minLength: number;
      historyCount: number;
    };
    lockout: {
      maxAttempts: number;
      includeMfa: boolean;
    };
  };

  // API Configuration
  api: {
    baseUrl: string;
    timeout: number;
  };

  // Footer & Legal
  legal: {
    copyright: string;
    company: string;
    links: {
      privacy?: string;
      terms?: string;
      support?: string;
    };
  };
}


/**
 * Default CoreX Configuration
 * 
 * When forking this project, create a new config file or modify these values.
 */
export const projectConfig: ProjectConfig = {
  // ==========================================
  // BRANDING - Update for your module
  // ==========================================
  name: 'CoreX',
  tagline: 'Enterprise SaaS Platform',
  description: 'The foundational framework for CoreX-Suite modules',
  version: '5.0.0',

  // ==========================================
  // LOGOS - Update paths for your module
  // ==========================================
  logos: {
    dark: corexLogoDark,
    light: corexLogoLight,
    icon: '/favicons/corex-favicon.svg',
  },

  // ==========================================
  // THEME CONFIGURATION
  // ==========================================
  theme: {
    default: 'system',      // Detect system preference
    showTutorial: true,     // Show tutorial on first visit
  },

  // ==========================================
  // AUTHENTICATION CONFIGURATION
  // DO NOT MODIFY - CoreX Security Standards
  // ==========================================
  auth: {
    mfa: {
      enabled: true,
      methods: ['TOTP', 'EMAIL_OTP'],
      trustDeviceDays: 30,
    },
    session: {
      // SECURITY (CVE-CRX-016): Reduced timeouts for better security
      inactivityTimeoutMinutes: 30, // 30 minutes inactivity
      absoluteTimeoutMinutes: 120, // 2 hours absolute max
      singleSession: true,
    },
    password: {
      minLength: 8,
      historyCount: 12,
    },
    lockout: {
      maxAttempts: 3,
      includeMfa: true,
    },
  },

  // ==========================================
  // API CONFIGURATION
  // ==========================================
  api: {
    baseUrl: import.meta.env.VITE_API_URL || '/api/v1',
    timeout: 30000, // 30 seconds
  },

  // ==========================================
  // LEGAL & FOOTER
  // ==========================================
  legal: {
    copyright: `© ${new Date().getFullYear()} CoreX-Suite`,
    company: 'CoreX-Suite',
    links: {
      privacy: '/privacy',
      terms: '/terms',
      support: 'mailto:support@corex-suite.com',
    },
  },
};


// ============================================================================
// MODULE-SPECIFIC CONFIGURATIONS
// ============================================================================

/**
 * VentaCoreX Configuration
 * Fork this for the Sales Intelligence module
 */
export const ventaCoreXConfig: ProjectConfig = {
  ...projectConfig,
  name: 'VentaCoreX',
  tagline: 'Enterprise Sales Intelligence',
  description: 'AI-powered sales management for Francophone Africa',
  logos: {
    dark: ventaLogoDark,
    light: ventaLogoLight,
    icon: '/favicons/ventacorex-favicon.svg',
  },
  legal: {
    ...projectConfig.legal,
    company: 'VentaCoreX',
    copyright: `© ${new Date().getFullYear()} VentaCoreX by CoreX-Suite`,
  },
};

/**
 * HRCoreX Configuration
 * Fork this for the HR Management module
 */
export const hrCoreXConfig: ProjectConfig = {
  ...projectConfig,
  name: 'HRCoreX',
  tagline: 'Human Resources Management',
  description: 'Complete HR solution for modern enterprises',
  logos: {
    dark: hrLogoDark,
    light: hrLogoLight,
    icon: '/favicons/hrcorex-favicon.svg',
  },
  legal: {
    ...projectConfig.legal,
    company: 'HRCoreX',
    copyright: `© ${new Date().getFullYear()} HRCoreX by CoreX-Suite`,
  },
};

/**
 * ComptaCoreX Configuration
 * Fork this for the Accounting module
 */
export const comptaCoreXConfig: ProjectConfig = {
  ...projectConfig,
  name: 'ComptaCoreX',
  tagline: 'Enterprise Accounting',
  description: 'Professional accounting and financial management',
  logos: {
    dark: comptaLogoDark,
    light: comptaLogoLight,
    icon: '/favicons/comptacorex-favicon.svg',
  },
  legal: {
    ...projectConfig.legal,
    company: 'ComptaCoreX',
    copyright: `© ${new Date().getFullYear()} ComptaCoreX by CoreX-Suite`,
  },
};

/**
 * PMCoreX Configuration
 * Fork this for the Project Management module
 */
export const pmCoreXConfig: ProjectConfig = {
  ...projectConfig,
  name: 'PMCoreX',
  tagline: 'Project Management',
  description: 'Enterprise project and task management',
  logos: {
    dark: pmLogoDark,
    light: pmLogoLight,
    icon: '/favicons/pmcorex-favicon.svg',
  },
  legal: {
    ...projectConfig.legal,
    company: 'PMCoreX',
    copyright: `© ${new Date().getFullYear()} PMCoreX by CoreX-Suite`,
  },
};


// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the current project configuration
 * This can be extended to read from environment or runtime config
 */
export function getConfig(): ProjectConfig {
  // Check for module-specific override
  const moduleEnv = import.meta.env.VITE_COREX_MODULE;
  
  switch (moduleEnv) {
    case 'ventacorex':
      return ventaCoreXConfig;
    case 'hrcorex':
      return hrCoreXConfig;
    case 'comptacorex':
      return comptaCoreXConfig;
    case 'pmcorex':
      return pmCoreXConfig;
    default:
      return projectConfig;
  }
}

/**
 * Get logo based on current theme
 */
export function getLogo(isDark: boolean, config: ProjectConfig = projectConfig): string {
  return isDark ? config.logos.dark : config.logos.light;
}


export default projectConfig;
